import { sortBy } from "underscore";

export const filterTicketsWithSearch = (
  tickets,
  searchText,
  editingTicketId,
  programBadgeTicketId,
) => {
  if (!searchText) {
    return tickets;
  }
  const searchTerms = searchText.split(" ");
  const filteredTickets = tickets
    .map((ticket) => {
      const {
        displayName,
        displayEmail,
        attendeeName,
        attendeeEmail,
        order,
        id,
      } = ticket;
      const orderId = order?.id ?? "";
      const matchCount = searchTerms.reduce((acc, term) => {
        return (
          acc +
          [
            displayName,
            displayEmail,
            attendeeName,
            attendeeEmail,
            orderId,
          ].reduce((acc, field) => {
            const match =
              field && field.toLowerCase().includes(term.toLowerCase()) ? 1 : 0;
            return acc + match;
          }, 0)
        );
      }, 0);
      return {
        ...ticket,
        matchCount,
        isEditing: editingTicketId === id,
        isProgramming: programBadgeTicketId === id,
      };
    })
    .filter(({ matchCount, isEditing }) => matchCount > 0 || isEditing);
  return sortBy(filteredTickets, ({ matchCount, isEditing, isProgramming }) => {
    if (isEditing || isProgramming) {
      return -1;
    }
    return -matchCount;
  });
};

export const filterTicketsBySession = (
  tickets,
  selectedSessionId,
  sessions,
) => {
  if (Boolean(sessions) && Boolean(selectedSessionId)) {
    const selectedSession = sessions.find(({ id }) => selectedSessionId === id);
    const { ticketTypes, ticketTypeSelection } = selectedSession || {};
    return tickets.filter(
      ({ ticketTypeKey }) =>
        ticketTypeSelection === "all" || ticketTypes.includes(ticketTypeKey),
    );
  } else {
    return tickets;
  }
};

export const filterByTicketType = (tickets, selectedTicketTypeKey) => {
  if (Boolean(selectedTicketTypeKey)) {
    return tickets.filter(
      ({ ticketTypeKey }) => ticketTypeKey === selectedTicketTypeKey,
    );
  } else {
    return tickets;
  }
};

export const getOrderGroupKey = (ticket) =>
  ticket.order?.id ?? `no-order-${ticket.id}`;

/**
 * Returns whether a ticket is checked in for the given session (or event-level if no sessionId).
 */
export const isTicketCheckedIn = (ticket, sessionId) => {
  if (!ticket) return false;
  const { checkIn, sessionCheckIns } = ticket;
  if (sessionId) {
    return Boolean((sessionCheckIns || {})[sessionId]);
  }
  return Boolean(checkIn);
};

/**
 * Filters tickets by check-in status.
 * @param {Array} tickets - Tickets to filter
 * @param {string|null} sessionId - Session ID for session-level check-in, or null for event-level
 * @param {string} status - "" (all), "checked-in", or "not-checked-in"
 */
export const filterByCheckInStatus = (tickets, sessionId, status) => {
  if (!status) return tickets;
  return tickets.filter((ticket) => {
    const checkedIn = isTicketCheckedIn(ticket, sessionId);
    return status === "checked-in" ? checkedIn : !checkedIn;
  });
};

/**
 * Returns a map of order group key to ticket count for each order.
 * Used to show total tickets per order in group headers (before search filter).
 */
export const calculateOrderGroupCounts = (tickets) => {
  const counts = {};
  (tickets || []).forEach((ticket) => {
    const key = getOrderGroupKey(ticket);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

/**
 * Extracts purchaser name from a ticket using purchaserOrderFormFields,
 * purchaserFirstName/purchaserLastName, or purchaserName.
 * Returns empty string if no purchaser name is found.
 */
export const getPurchaserName = (ticket) => {
  if (!ticket) return "";
  const {
    purchaserFirstName,
    purchaserLastName,
    purchaserName,
    purchaserOrderFormFields,
  } = ticket;

  if (purchaserFirstName && purchaserLastName) {
    return `${purchaserFirstName} ${purchaserLastName}`.trim();
  }
  if (purchaserName) return String(purchaserName).trim();

  const fields = purchaserOrderFormFields || [];
  const firstNameField = fields.find(({ key }) => key === "firstName");
  const lastNameField = fields.find(({ key }) => key === "lastName");
  const nameField = fields.find(({ key }) => key === "name");

  if (firstNameField?.value && lastNameField?.value) {
    return `${firstNameField.value} ${lastNameField.value}`.trim();
  }
  if (nameField?.value) return String(nameField.value).trim();
  if (firstNameField?.value) return String(firstNameField.value).trim();
  if (lastNameField?.value) return String(lastNameField.value).trim();

  return "";
};

export const filterByGroupSize = (tickets, selectedGroupSize) => {
  if (!selectedGroupSize) {
    return tickets;
  }
  const groupSize = parseInt(selectedGroupSize, 10);
  
  // Group tickets by order ID; tickets with no order are each their own group of 1
  const orderGroups = {};
  tickets.forEach((ticket) => {
    const orderId = getOrderGroupKey(ticket);
    if (!orderGroups[orderId]) {
      orderGroups[orderId] = [];
    }
    orderGroups[orderId].push(ticket);
  });

  // Filter to only include tickets from orders of the selected size
  const orderIdsWithSelectedSize = Object.keys(orderGroups).filter(
    (orderId) => orderGroups[orderId].length === groupSize,
  );

  return tickets.filter((ticket) => {
    const orderId = getOrderGroupKey(ticket);
    return orderIdsWithSelectedSize.includes(orderId);
  });
};

export const ticketStats = (tickets) => {
  const checkedInCount = tickets.filter(({ checkIn }) =>
    Boolean(checkIn),
  ).length;
  const ticketCount = tickets.length;
  const notCheckedInCount = ticketCount - checkedInCount;
  const checkInPercent = ~~((checkedInCount / ticketCount) * 100);
  const notCheckInPercent = 100 - checkInPercent;
  return {
    checkedInCount,
    ticketCount,
    notCheckedInCount,
    checkInPercent,
    notCheckInPercent,
  };
};

export const calculateGroupSizes = (tickets) => {
  // Group tickets by order ID; tickets with no order are each their own group of 1
  const orderGroups = {};
  tickets.forEach((ticket) => {
    const orderId = getOrderGroupKey(ticket);
    if (!orderGroups[orderId]) {
      orderGroups[orderId] = [];
    }
    orderGroups[orderId].push(ticket);
  });

  // Count how many groups of each size
  const groupSizeCounts = {};
  Object.values(orderGroups).forEach((group) => {
    const size = group.length;
    groupSizeCounts[size] = (groupSizeCounts[size] || 0) + 1;
  });

  // Convert to array format for charting, sorted by group size
  const groupSizeData = Object.keys(groupSizeCounts)
    .map((size) => ({
      groupSize: parseInt(size, 10),
      count: groupSizeCounts[size],
    }))
    .sort((a, b) => a.groupSize - b.groupSize);

  return groupSizeData;
};

/**
 * Sorts tickets so that tickets from the same order appear consecutively,
 * with order groups sorted by purchaser name (using first ticket in each group).
 * Tickets with no order appear last, each as their own "group".
 */
export const sortTicketsByOrder = (tickets) => {
  if (!tickets || tickets.length === 0) {
    return tickets;
  }
  const orderGroups = {};
  tickets.forEach((ticket) => {
    const orderId = getOrderGroupKey(ticket);
    if (!orderGroups[orderId]) {
      orderGroups[orderId] = [];
    }
    orderGroups[orderId].push(ticket);
  });

  const sortedGroups = Object.values(orderGroups).sort((groupA, groupB) => {
    const purchaserNameA =
      getPurchaserName(groupA[0]) || getOrderGroupKey(groupA[0]);
    const purchaserNameB =
      getPurchaserName(groupB[0]) || getOrderGroupKey(groupB[0]);
    return purchaserNameA.localeCompare(purchaserNameB, undefined, {
      sensitivity: "base",
    });
  });

  return sortedGroups.flat();
};
